import { useEffect, type PropsWithChildren } from 'react'

import { useSearchParams } from 'react-router-dom'
import { gridState, gridStateActions } from './grid.store'
import { GRID_DOM_OWNERSHIP } from './grid.const'

export function Grid({ children }: PropsWithChildren) {
  // #region ============== SearchParams

  const [searchParams] = useSearchParams()

  useEffect(() => {
    const editableColumns = searchParams.get('edit-columns')?.split(',') || []
    gridState.editableColumns = editableColumns
  }, [searchParams])

  // #endregion

  // #region ============== EVENTS

  /**
   * Event handler attached to the document.
   * If anything other than a cell is clicked, cell selection is cleared.
   */
  const clearSelectionHandler = (e: Event) => {
    if (!(e.target instanceof HTMLElement)) return

    const targetIsNotCell = !e.target.closest('[role="cell"]')

    if (targetIsNotCell) {
      gridStateActions.resetSelectedCell()
    }
  }

  /**
   * Prevents browser defaults for drag and drop on the entire document.
   *
   * By default browsers will show the "copy" cursor on drag and open the file
   * in a new tab on drop. This hook prevents that.
   *
   * Components that need drag and drop can register local event handlers and call
   * `preventDefault()` and/or `stopPropagation()` to override behaviour as needed.
   */
  const handleDragOver = (e: DragEvent) => {
    if (!(e.target instanceof Element)) return

    // Ignore TipTap editors (they can handle events using `handleDrop`)
    const isEditor = e.target.closest('.tiptap') !== null
    if (isEditor) return

    // Prevent drop for all document targets
    e.preventDefault()
    e.stopPropagation()

    // Prevent 'copy' cursor for all document targets
    if (!e.dataTransfer) return
    e.dataTransfer.dropEffect = 'none'
  }

  useEffect(() => {
    document.addEventListener('click', clearSelectionHandler)
    // We only need to handle `dragover` to prevent the behaviour.
    // The other drag* events are for visual changes on enter/leave/drop.
    document.addEventListener('dragover', handleDragOver)
    return () => {
      document.removeEventListener('click', clearSelectionHandler)
      document.removeEventListener('dragover', handleDragOver)
    }
  }, [])

  // #endregion

  return (
    <div
      role="grid"
      aria-owns={GRID_DOM_OWNERSHIP}
      aria-label="Rundown grid"
      className="pb-6"
    >
      {children}
    </div>
  )
}
